import React from 'react';
import Modal from '@/shared/components/Modal';
import { BodyText, H2 } from '@/shared/components/StyledText';
import styled, { css } from '@emotion/native';
import Button from '@/shared/components/Button';
import { sand, white } from '@/shared/styles/colors';
import ModalWrapper from '@/shared/styles/components/ModalWrapper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useMutation } from '@apollo/client';
import { MutationResult } from '@/shared/types/generated';
import { DELETE_USER_ACCOUNT } from '@/shared/mutations';
import SentryService from '@/services/sentry/Sentry.service';
import { useApollo } from '@/services/apollo/ApolloProvider';

interface Props {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
}

const ConfirmDeleteAccountModal = ({ isVisible, setIsVisible }: Props) => {
  const insets = useSafeAreaInsets();
  const { logOut } = useApollo();

  const [deleteUserAccount] =
    useMutation<MutationResult<'deleteUserAccount'>>(DELETE_USER_ACCOUNT);

  const onPressConfirm = async () => {
    try {
      await deleteUserAccount();
      setIsVisible(false);
      logOut();
    } catch (error) {
      SentryService.captureError('Error sending delete account request', error);
    }
  };

  const onPressCancel = () => {
    setIsVisible(false);
  };

  return (
    <Modal isVisible={isVisible} setIsVisible={setIsVisible}>
      <ModalWrapper style={containerStyle}>
        <CloseButton
          onPress={() => setIsVisible(false)}
          style={{ top: insets.top + 21 }}
        >
          <CloseButtonImage
            source={require('@/assets/images/CloseGreen.png')}
          />
        </CloseButton>
        <Title>Delete your account?</Title>
        <Body>
          This will permanently delete your account and all associated session
          history. This action can't be undone.
        </Body>
        <Button
          onPress={onPressCancel}
          text="On second thought.."
          customStyle={{ marginBottom: 24 }}
        />
        <Button
          onPress={onPressConfirm}
          text="Delete Account"
          variant="secondary"
          customStyle={{ backgroundColor: white }}
        />
      </ModalWrapper>
    </Modal>
  );
};

export default ConfirmDeleteAccountModal;

const containerStyle = css({
  flex: 1,
  width: '100%',
  maxWidth: 697,
  maxHeight: 1247,
  backgroundColor: sand,
  borderRadius: 12,
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: 28,
});

const CloseButton = styled.TouchableOpacity({
  position: 'absolute',
  right: 23,
});

const CloseButtonImage = styled.Image({
  width: 24,
  height: 24,
});

const Title = styled(H2)({
  marginBottom: 24,
  textTransform: 'uppercase',
  textAlign: 'center',
});

const Body = styled(BodyText)({
  width: '90%',
  marginBottom: 36,
  fontSize: 16,
  lineHeight: 18,
  textAlign: 'center',
});
